import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {BASE_URL} from '@/const';
import {testEmail} from '@/helper';
import EmailIcon from '@/icons/EmailIcon';
import LockIcon from '@/icons/LockIcon';
import useHandleBackendErrors from '@/hooks/useHandleBackendErrors';
import useSignIn from '@/queries/auth/useSignIn';
import Button, {BTN_TYPES} from '@/ui/Button';
import Checkbox from '@/ui/Checkbox';
import Input from '@/ui/Input';
import Overlay from '@/ui/Overlay';
import RelatedAdsLogoMedia from '@/ui/RelatedAdsLogoMedia';
import {AUTH_PATHNAME, AuthSteps, ILoginFormsProps} from '../../interface';
import AdditionalAction from '../AdditionalAction';
import StepTitle from '../StepTitle';
import styles from './styles.module.scss';

interface IFormValues {
    user_login: string;
    user_password: string;
    remember: boolean;
}

const LoginForm = ({setStep = () => {}}: ILoginFormsProps) => {
    const {mutate, isPending, error, isSuccess} = useSignIn();

    const formMethods = useForm<IFormValues>({
        values: {
            user_login: '',
            user_password: '',
            remember: false
        }
    });

    const {
        handleSubmit,
        register,
        setError,
        formState: {errors}
    } = formMethods;

    const openRegister = () => window.open(AUTH_PATHNAME.register, '_self');

    const onSubmit: SubmitHandler<IFormValues> = data => {
        mutate({data}, {onSuccess: () => window.open(BASE_URL, '_self')});
    };

    useHandleBackendErrors({error, setError});

    return (
        <FormProvider {...formMethods}>
            <Overlay visible={isSuccess} />
            <RelatedAdsLogoMedia />
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form} noValidate>
                <StepTitle title={'Log in'} />
                <div className={styles.inputs}>
                    <Input
                        error={errors?.user_login?.message}
                        Icon={EmailIcon}
                        inputProps={{
                            placeholder: 'E-mail',
                            type: 'email',
                            ...register('user_login', {
                                required: 'E-mail is required',
                                validate: {
                                    matchPattern: v => testEmail(v) || 'E-mail is invalid'
                                }
                            })
                        }}
                    />
                    <Input
                        error={errors?.user_password?.message}
                        Icon={LockIcon}
                        inputProps={{
                            type: 'password',
                            placeholder: 'Password',
                            ...register('user_password', {required: 'Password is required'})
                        }}
                    />
                    <div className={styles.bottom}>
                        <Checkbox label={'Remember me'} checkboxProps={{...register('remember')}} />
                        <Button
                            btnType={BTN_TYPES.opacity}
                            onClick={() => setStep(AuthSteps.forgot)}
                            btnProps={{type: 'button'}}
                            btnStyles={[styles.forgot]}
                        >
                            Forgot password?
                        </Button>
                    </div>
                </div>
                <div className={styles.footer}>
                    <Button loading={isPending} btnStyles={[styles.submit]}>
                        Login
                    </Button>
                    <AdditionalAction text={'Don’t have an account yet?'} btnText={'Sign up'} onClick={openRegister} />
                </div>
            </form>
        </FormProvider>
    );
};

export default LoginForm;
